import { FC, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { DropdownFilter, DropdownSelect, useDropdown } from 'shared/components/DropdownFilter';
import { useFilterChangeCallback, useShowSearchMetaTooltip } from 'shared/hooks';
import { selectCommercialPriceTypeFilter } from 'shared/selectors/filters/commercialPriceType';
import { selectOfferType } from 'shared/selectors/jsonQuery';
import { selectIsInlineFiltersRedesigned } from 'shared/selectors/redesigned/selectIsInlineFiltersRedesigned';
import { TModifier } from 'shared/types/filters';

import { EJsonQueryPriceType } from '../../../packages/api-models/common/json_query';
import { EPriceType, FOfferType } from '../../../packages/JsonQuery';

import { FILTER_KEY } from './constants';

const COWORKING_OR_WORKPLACE = FOfferType.Coworking | FOfferType.Workplace;

export const PriceTypeContainer: FC = () => {
  const { label, value, options } = useSelector(selectCommercialPriceTypeFilter);
  const offerType = useSelector(selectOfferType);
  const isFiltersRedesignEnabled = useSelector(selectIsInlineFiltersRedesigned);

  const dropdownState = useDropdown(FILTER_KEY);
  const showTooltip = useShowSearchMetaTooltip(FILTER_KEY);

  const onChange = useFilterChangeCallback(FILTER_KEY);

  const handleChange = useCallback(
    (nextValue: EPriceType) => {
      const isWorkplaceContext = (offerType & COWORKING_OR_WORKPLACE) !== 0;
      const modifiers: TModifier[] = [];

      if (nextValue === EPriceType.SM) {
        if (isWorkplaceContext) {
          modifiers.push({ action: 'setCommercialPriceType', arguments: [EJsonQueryPriceType.Workplace] });
          modifiers.push({ action: 'setPriceType', arguments: [null] });
        } else {
          modifiers.push({ action: 'setPriceType', arguments: [true] });
          modifiers.push({ action: 'setCommercialPriceType', arguments: [null] });
        }
      } else {
        modifiers.push({ action: 'setPriceType', arguments: [null] });
        modifiers.push({ action: 'setCommercialPriceType', arguments: [null] });
      }

      dropdownState.onClose();
      onChange(...(modifiers as [TModifier, ...TModifier[]]));
    },
    [offerType, onChange, dropdownState],
  );

  return (
    <DropdownFilter
      {...dropdownState}
      buttonLabel={label}
      showTooltip={showTooltip}
      isFiltersRedesignEnabled={isFiltersRedesignEnabled}
      buttonStyle="dashed"
      dropdownContent={<DropdownSelect options={options} value={value} onChange={handleChange as () => void} />}
    />
  );
};
